import React, { useEffect, useState } from "react";
import Layout from "../components/layout/Layout";
import RolesReferenceTable from "../components/admin/RolesReferenceTable";
import { useNavigate } from "react-router-dom";
import { getAllUsers, assignUserRole, getAllRoles } from "../api/userApi";
import axiosClient from "../api/axiosClient";
import {
  Alert,
  Snackbar,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Button,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { useBackNavigation } from "../hooks/useBackNavigation";
import { useBreadcrumb } from "../context/BreadcrumbContext";
import { useAuth } from "../context/AuthContext";
import OwnerPortalAccess from "../components/client/OwnerPortalAccess";

function AdminUserManagement({ embedded = false }) {
  const navigate = useNavigate();
  const goBack = useBackNavigation("/settings");
  const { setCustomPath, clearCustomPath } = useBreadcrumb();
  const { user } = useAuth();

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);

  // Toast state
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const userRoles = user?.claims_role || user?.roles || [];
  const canChangeRoles = userRoles.some((role) =>
    ["systemowner", "itsupport"].includes(String(role).toLowerCase())
  );
  const currentUserB2cId = user?.b2c_object_id;

  useEffect(() => {
    if (embedded) return undefined;
    setCustomPath([
      { label: "Settings", path: "/settings" },
      { label: "User Management", path: null },
    ]);
    return () => clearCustomPath();
  }, [setCustomPath, clearCustomPath, embedded]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, rolesData] = await Promise.all([
          getAllUsers(),
          getAllRoles(),
        ]);
        setUsers(usersData || []);
        setRoles(rolesData || []);
      } catch (err) {
        console.error("Error loading users/roles:", err);
        setError("Failed to load users or roles. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getRoleDisplayName = (roleName) => {
    if (!roleName) return "No role";
    const role = roles.find((r) => r.name === roleName);
    return role?.display_name || roleName;
  };

  const handleRoleChange = async (userId, newRoleId) => {
    setError("");
    setUpdating(userId);

    try {
      await assignUserRole(userId, newRoleId);

      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === userId
            ? {
                ...u,
                role: roles.find((r) => r.id === newRoleId)?.name || u.role,
                role_display_name:
                  roles.find((r) => r.id === newRoleId)?.display_name ||
                  u.role_display_name,
              }
            : u
        )
      );

      showToast("Role updated.");
    } catch (err) {
      console.error("Error updating role:", err);
      showToast("Failed to update role. Please try again.", "error");
    } finally {
      setUpdating(null);
    }
  };

  const content = (
    <Box className="page-container">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        {!embedded && (
          <Button variant="outlined" onClick={() => goBack()}>
            Back
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Paper sx={{ p: 3 }}>
          <Skeleton height={40} width="100%" sx={{ mb: 2 }} />
          <Skeleton height={60} width="100%" sx={{ mb: 1 }} />
          <Skeleton height={60} width="100%" sx={{ mb: 1 }} />
          <Skeleton height={60} width="100%" />
        </Paper>
      ) : (
        <Paper sx={{ p: 3, borderRadius: 2 }} elevation={1}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Current Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Assignments</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Change Role</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No users found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => {
                    const isCurrentUser = Boolean(
                      currentUserB2cId && u.b2c_object_id === currentUserB2cId
                    );

                    return (
                      <TableRow key={u.user_id} hover>
                        <TableCell>
                          <Typography fontWeight={500}>{u.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {u.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {u.role_display_name || getRoleDisplayName(u.role)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {!u.is_global_role && u.role ? (
                            <Typography variant="body2" color="text.secondary">
                              {u.property_count ?? u.property_ids?.length ?? 0} properties,{" "}
                              {u.unit_count ?? u.unit_ids?.length ?? 0} units
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 180 }}>
                              <Select
                                value={roles.find((r) => r.name === u.role)?.id || ""}
                                onChange={(e) =>
                                  handleRoleChange(u.user_id, Number(e.target.value))
                                }
                                disabled={
                                  updating === u.user_id || !canChangeRoles || isCurrentUser
                                }
                                displayEmpty
                              >
                                <MenuItem value="" disabled>
                                  <em>Select Role</em>
                                </MenuItem>
                                {roles.map((r) => (
                                  <MenuItem key={r.id} value={r.id}>
                                    {r.display_name || r.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {updating === u.user_id && (
                              <CircularProgress size={20} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={
                              updating === u.user_id || u.is_global_role || isCurrentUser
                            }
                            onClick={() =>
                              navigate(`/admin/users/${u.user_id}/assignments`)
                            }
                          >
                            Manage Assignments
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {!loading && <OwnerPortalAccess />}
      {!loading && <RolesReferenceTable roles={roles} sx={{ mt: 3 }} />}

      {/* TOAST / SNACKBAR */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );

  if (embedded) {
    return content;
  }

  return (
    <Layout>
      {content}
    </Layout>
  );
}

export default AdminUserManagement;
